import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  Position,
  useInternalNode,
  type EdgeProps,
} from "@xyflow/react";
import * as React from "react";

/**
 * Edge of the CASCADE layout (cascade.ts). Two shapes:
 *
 * - forward: right-middle of the source → left-middle of the target, a horizontal bezier — the
 *   cascade only ever points right, so the edge never needs to float to another side;
 * - back (`data.back`): a loop to an EARLIER step (retry / fix), drawn as a dashed arc that dips
 *   under every node it passes (`data.floorY`, computed by the layout) on its own `data.lane`, so
 *   loops read as "goes back" and never cut through the pills.
 *
 * `data.taken` paints it charts-green like FloatingEdge — the run walked it.
 */
export function CascadeEdge({
  id,
  source,
  target,
  markerEnd,
  style,
  label,
  selected,
  data,
}: EdgeProps) {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  if (!sourceNode || !targetNode) {
    return null;
  }
  const d = (data ?? {}) as { taken?: boolean; back?: boolean; floorY?: number; lane?: number };
  const taken = d.taken === true;
  const edgeStyle: React.CSSProperties = {
    ...(style as React.CSSProperties),
    ...(d.back ? { strokeDasharray: "6 4" } : {}),
    ...(taken ? { stroke: "var(--vscode-charts-green, #2faa5a)", strokeWidth: 2.5 } : {}),
  };

  const sp = sourceNode.internals.positionAbsolute;
  const tp = targetNode.internals.positionAbsolute;
  const sw = sourceNode.measured?.width ?? 0;
  const sh = sourceNode.measured?.height ?? 0;
  const th = targetNode.measured?.height ?? 0;

  let path: string;
  let labelX: number;
  let labelY: number;
  if (d.back) {
    // Out of the source's right side, down the column gap, along under the graph, up the gap left
    // of the target, into its left side: the legs live in the gaps between columns, so a loop
    // never cuts through a pill — not even one stacked under its source or its target. Each lane
    // nests one step further out, so overlapping loops stay apart.
    const lane = d.lane ?? 0;
    const sx = sp.x + sw;
    const sy = sp.y + sh / 2 + 8;
    const tx = tp.x;
    const ty = tp.y + th / 2 + 8;
    const rightX = sx + 22 + lane * 12;
    const leftX = tx - 22 - lane * 12;
    const floor = Math.max(d.floorY ?? -Infinity, sp.y + sh, tp.y + th);
    const dip = floor + 30 + lane * 22;
    path = roundedPath(
      [
        [sx, sy],
        [rightX, sy],
        [rightX, dip],
        [leftX, dip],
        [leftX, ty],
        [tx, ty],
      ],
      12,
    );
    labelX = (rightX + leftX) / 2;
    labelY = dip;
  } else {
    [path, labelX, labelY] = getBezierPath({
      sourceX: sp.x + sw,
      sourceY: sp.y + sh / 2,
      sourcePosition: Position.Right,
      targetX: tp.x,
      targetY: tp.y + th / 2,
      targetPosition: Position.Left,
    });
  }

  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={edgeStyle} />
      {label ? (
        <EdgeLabelRenderer>
          <div
            className={`fn-edge-label${selected ? " selected" : ""}${d.back ? " fn-edge-label-back" : ""}`}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
            }}
            title={typeof label === "string" ? label : undefined}
          >
            {d.back ? "↺ " : ""}
            {label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

/** A polyline with its corners rounded to radius `r` (clamped to half of each adjoining leg). */
function roundedPath(points: Array<[number, number]>, r: number): string {
  let out = `M ${points[0][0]},${points[0][1]}`;
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i - 1];
    const [cx, cy] = points[i];
    const [nx, ny] = points[i + 1];
    const inLen = Math.hypot(cx - px, cy - py);
    const outLen = Math.hypot(nx - cx, ny - cy);
    const k = Math.min(r, inLen / 2, outLen / 2);
    const ax = cx - ((cx - px) / (inLen || 1)) * k;
    const ay = cy - ((cy - py) / (inLen || 1)) * k;
    const bx = cx + ((nx - cx) / (outLen || 1)) * k;
    const by = cy + ((ny - cy) / (outLen || 1)) * k;
    out += ` L ${ax},${ay} Q ${cx},${cy} ${bx},${by}`;
  }
  const last = points[points.length - 1];
  return `${out} L ${last[0]},${last[1]}`;
}
